import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/components/Navbar";

const REASONS = [
  {
    n: "01",
    title: "A Story That Stops the Scroll",
    desc: "More than 580 million views across television, digital media, podcasts, documentaries, and social platforms.",
  },
  {
    n: "02",
    title: "An Audience That Shows Up",
    desc: "160,000+ combined followers, 19,000+ monthly podcast listeners, 37,000 email subscribers — and a committed circle of champions reaching 78M+ more.",
  },
  {
    n: "03",
    title: "A Guest Who Delivers",
    desc: "Thousands of interviews across every format — camera-tested, deeply quotable, and unforgettable in every segment.",
  },
  {
    n: "04",
    title: "Topics That Matter Now",
    desc: "Grief, resilience, mental health, suicide loss, and post-traumatic growth — told with warmth, credibility, and hope.",
  },
];

const TOPIC_GROUPS = [
  {
    label: "Survival & Trauma",
    topics: [
      "Surviving childhood sexual abuse — and reclaiming the story",
      "A child's escape from trafficking, and the healer it forged",
      "The brain tumor years (2009–2013) — and what they taught her",
      "Healing trauma at the root, not the surface",
    ],
  },
  {
    label: "Grief & Loss",
    topics: [
      "Losing a loved one to murder — grieving without closure",
      "Suicide loss — the conversation nobody is having",
      "What we get wrong about grief",
      "Hope after unimaginable loss",
    ],
  },
  {
    label: "Family & Addiction",
    topics: [
      "When addiction lives in the family",
      "Loving someone through their darkest season",
      "Mothers estranged from their children — the silent grief",
      "Infertility, adoption & the making of a mother",
      "Breaking the cycles we inherited",
    ],
  },
  {
    label: "Women Rising",
    topics: [
      "Leaving abuse — and finding yourself on the other side",
      "Women's empowerment after trauma",
      "The strength we never knew we had",
      "From tragedy to the talk-show stage",
    ],
  },
  {
    label: "Spirit & the Afterlife",
    topics: [
      "Susan's near-death experience — and what came back with her",
      "Signs from loved ones on the other side",
      "Intuition as a life skill anyone can learn",
      "Bridging the living and the spirit world",
    ],
  },
  {
    label: "Healing & Transformation",
    topics: [
      "Shadow work — why everyone is talking about it",
      "Mirror work: turning insight into change",
      "Post-traumatic growth after the unthinkable",
      "Learning to love yourself into enough",
    ],
  },
];

export default function ProducerPitch() {
  return (
    <section id="for-producers" data-testid="producer-pitch" className="relative overflow-hidden bg-[#F7F1E6] py-28 lg:py-40">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="font-body text-[0.65rem] font-medium uppercase tracking-[0.5em] text-[#C5A059]"
        >
          For Producers & Editors
        </motion.p>
        <div className="mt-6 flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.76, 0, 0.24, 1] }}
            className="font-display2 uppercase leading-[0.88] tracking-[0.01em] text-[#6B5D4F] text-5xl sm:text-6xl lg:text-8xl"
          >
            Why the biggest stages <br />
            <span className="font-display normal-case italic tracking-normal text-[#C5A059]">call Susan.</span>
          </motion.h2>
          <motion.button
            data-testid="producer-pitch-cta"
            onClick={() => scrollToId("#press")}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="group flex w-fit items-center gap-3 border border-[#6B5D4F] px-7 py-3.5 font-body text-[0.65rem] font-medium uppercase tracking-[0.3em] text-[#6B5D4F] transition-colors duration-500 hover:border-[#C5A059] hover:text-[#C5A059]"
          >
            Start the Conversation
            <ArrowUpRight size={16} className="transition-transform duration-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </motion.button>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-px border border-[#6B5D4F]/10 bg-[#6B5D4F]/10 md:grid-cols-2 lg:grid-cols-4">
          {REASONS.map((r, i) => (
            <motion.div
              key={r.n}
              data-testid={`pitch-reason-${r.n}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.7, delay: i * 0.08, ease: [0.76, 0, 0.24, 1] }}
              className="group bg-[#FDFAF4] p-9 transition-colors duration-500 hover:bg-[#6B5D4F]"
            >
              <span className="font-display text-xl italic text-[#C5A059]">{r.n}</span>
              <h3 className="mt-5 font-display text-2xl font-light leading-snug tracking-tight text-[#6B5D4F] transition-colors duration-500 group-hover:text-[#FDFAF4]">
                {r.title}
              </h3>
              <p className="mt-4 font-body text-sm font-light leading-relaxed text-[#8B7D6B] transition-colors duration-500 group-hover:text-[#FDFAF4]/60">
                {r.desc}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.1 }}
          className="mt-20"
        >
          <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
            <p className="font-body text-[0.6rem] font-medium uppercase tracking-[0.4em] text-[#8B7D6B]">
              The conversation index — a sampling of what Susan can speak to
            </p>
            <p className="font-script text-2xl text-[#C5A059]">and so much more</p>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-px border border-[#6B5D4F]/10 bg-[#6B5D4F]/10 sm:grid-cols-2 lg:grid-cols-3">
            {TOPIC_GROUPS.map((g, gi) => (
              <div key={g.label} data-testid={`topic-group-${gi}`} className="bg-[#FDFAF4] p-8">
                <h3 className="font-display text-xl font-light italic leading-snug text-[#C5A059]">{g.label}</h3>
                <ul className="mt-6">
                  {g.topics.map((t, ti) => (
                    <li
                      key={t}
                      data-testid={`topic-${gi}-${ti}`}
                      className="border-t border-[#6B5D4F]/10 py-4 font-body text-sm font-light leading-relaxed text-[#6B5D4F]"
                    >
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
